// The allowed-action table of §2.14, encoded as data. It is authoritative: where §2.6's prose
// list of grabbable states differs (PUNCH, FLOAT), this table wins. Durations live in TUNE.
import { BS, ItemKind } from '../ids';
import { TUNE } from '../constants';

/**
 * Move column: 'full' normal steering; 'scaled' normal steering at `moveScale` × run speed;
 * 'locked' lunge motion only; 'di' directional influence only; 'recover' teeter recovery input
 * only; 'ledge' climb/drop input only; 'float' balloon air control; 'ghost' ghost flight;
 * 'descend' parachute steering at `moveScale`; 'none' no control.
 */
export type MoveRule = 'full' | 'scaled' | 'locked' | 'di' | 'recover' | 'ledge' | 'float' | 'ghost' | 'descend' | 'none';
/** 'carry' jumps with the victim; 'mash' counts toward escape; 'hop' hops inward; 'climb' climbs. */
export type JumpRule = 'yes' | 'no' | 'carry' | 'mash' | 'hop' | 'climb';
/** 'drop' drops the carried victim then lunges; 'pebble' drops a ghost pebble. */
export type ShoveRule = 'yes' | 'no' | 'drop' | 'mash' | 'pebble';
export type GrabRule = 'yes' | 'no' | 'throw' | 'mash';
/** 'dz' grabbable/shovable only when |Δz| ≤ TUNE.balloon.hitDz. */
export type GrabbableRule = 'yes' | 'no' | 'dz';
/**
 * Hit response: 'yes' normal; 'drops' carrier drops its victim; 'frees' grabbed victim is
 * released; 'falls' goes to FALLING; 'cancels' cancels the windup; 'pops' pops the balloon;
 * 'no' not hittable.
 */
export type HitRule = 'yes' | 'no' | 'drops' | 'frees' | 'falls' | 'cancels' | 'pops';
/** Edge column: 'rule' §2.9 table; 'teeter' always TEETER; 'ruleDrop' §2.9 and drops the victim; 'none' n/a. */
export type EdgeRule = 'rule' | 'teeter' | 'ruleDrop' | 'none';

export interface StateRow {
  move: MoveRule;
  moveScale: number;
  jump: JumpRule;
  shove: ShoveRule;
  grab: GrabRule;
  /** Items usable in this state (FALLING's Plunger additionally requires z > TUNE.plunger.fallingMinZ). */
  items: readonly ItemKind[];
  grabbable: GrabbableRule;
  hit: HitRule;
  edge: EdgeRule;
}

const ALL_ITEMS: readonly ItemKind[] = [ItemKind.BANANA, ItemKind.BOMB, ItemKind.GLOVE, ItemKind.BALLOON, ItemKind.PLUNGER, ItemKind.SHAKE];
const NO_ITEMS: readonly ItemKind[] = [];

function row(r: Partial<StateRow> & Pick<StateRow, 'move'>): StateRow {
  return { moveScale: 1, jump: 'no', shove: 'no', grab: 'no', items: NO_ITEMS, grabbable: 'no', hit: 'yes', edge: 'none', ...r };
}

export const STATE_TABLE: Readonly<Record<BS, StateRow>> = {
  [BS.NORMAL]:       row({ move: 'full', jump: 'yes', shove: 'yes', grab: 'yes', items: ALL_ITEMS, grabbable: 'yes', edge: 'rule' }),
  [BS.LUNGE]:        row({ move: 'locked', grabbable: 'yes', edge: 'teeter' }),
  [BS.TUMBLE]:       row({ move: 'di', items: [ItemKind.BALLOON, ItemKind.PLUNGER], grabbable: 'yes', edge: 'rule' }),
  [BS.GRAB_START]:   row({ move: 'scaled', moveScale: TUNE.grab.startupSpeed, grabbable: 'yes', edge: 'teeter' }),
  [BS.GRAB_MISS]:    row({ move: 'scaled', moveScale: TUNE.grab.missSpeed, grabbable: 'yes', edge: 'teeter' }),
  [BS.CARRYING]:     row({ move: 'scaled', moveScale: TUNE.grab.carrySpeed, jump: 'carry', shove: 'drop', grab: 'throw', hit: 'drops', edge: 'ruleDrop' }),
  [BS.GRABBED]:      row({ move: 'none', jump: 'mash', shove: 'mash', grab: 'mash', hit: 'frees' }),
  [BS.TEETER]:       row({ move: 'recover', jump: 'hop', items: [ItemKind.BALLOON], grabbable: 'yes', hit: 'falls' }),
  [BS.LEDGE]:        row({ move: 'ledge', jump: 'climb', hit: 'falls' }),
  [BS.CLIMB]:        row({ move: 'none', hit: 'falls' }),
  [BS.SLIP]:         row({ move: 'none', grabbable: 'yes', edge: 'rule' }),
  [BS.SPUN]:         row({ move: 'none', hit: 'no' }),
  [BS.YANKED]:       row({ move: 'none' }),
  [BS.PUNCH_WINDUP]: row({ move: 'scaled', moveScale: TUNE.glove.windupSpeed, grabbable: 'yes', hit: 'cancels', edge: 'teeter' }),
  [BS.PUNCH]:        row({ move: 'none', grabbable: 'yes', edge: 'teeter' }),
  [BS.FLOAT]:        row({ move: 'float', grabbable: 'dz', hit: 'pops' }),
  [BS.FALLING]:      row({ move: 'none', items: [ItemKind.PLUNGER], hit: 'no' }),
  [BS.GHOST]:        row({ move: 'ghost', shove: 'pebble', hit: 'no' }),
  [BS.RESPAWN]:      row({ move: 'descend', moveScale: TUNE.respawn.paraSpeed, hit: 'no' }),
  [BS.FROZEN]:       row({ move: 'none', hit: 'no' }),
};

export function canUseItem(state: BS, kind: ItemKind): boolean {
  return STATE_TABLE[state].items.includes(kind);
}

export function isHittable(state: BS): boolean {
  return STATE_TABLE[state].hit !== 'no';
}
